import { useState, type ReactNode } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Use instead of a native `title`, which has no affordance and never reaches keyboard or screen-reader users.
 * Copy of `InfoHint` in `@dolfin/ui` (sandbox doesn't depend on it yet); keep the two in step.
 */
export function InfoHint({
  children,
  label,
  side = "top",
  className = "",
}: {
  /** The explanation. A sentence or two - anything longer belongs on the page. */
  children: ReactNode;
  /** Names what is being explained, for screen readers: "More about Automated". */
  label: string;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}) {
  // Controlled so a tap opens it too: Radix tooltips are pointer- and focus-driven,
  // which leaves touch users with no way in.
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen} delayDuration={150}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`More about ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            className={`inline-flex shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${className}`}
          >
            <Info className="size-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} sideOffset={4} collisionPadding={8} className="max-w-xs text-left leading-snug">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
